import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Phone, Mail, Inbox, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Anfragen({ user }) {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/anfragen`, { credentials: "include" });
        if (!res.ok) throw new Error();
        setItems(await res.json());
      } catch {
        setItems([]);
      }
    };
    load();
  }, []);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="anfragen-page">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5 md:px-8">
          <div className="flex items-center gap-3">
            <img src="/assets/terbeck-logo.jpeg" alt="Terbeck Service GbR Logo" className="h-9 w-9 rounded-md object-cover ring-1 ring-white/15" />
            <span className="font-display text-sm font-bold tracking-tight">
              ANFRAGEN<span className="text-brand-bright">-POSTFACH</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-white/50" data-testid="anfragen-user">{user?.email}</span>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-bold tracking-wider text-white hover:border-brand hover:text-brand-bright transition-colors duration-300"
            >
              <LogOut className="h-3.5 w-3.5" /> ABMELDEN
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 md:px-8 py-10 md:py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-brand-bright hover:text-white transition-colors duration-300">
          <ArrowLeft className="h-4 w-4" /> ZUR WEBSITE
        </Link>
        <h1 className="mt-6 font-display text-3xl md:text-4xl font-bold uppercase tracking-tighter">
          Eingegangene Anfragen
        </h1>
        <p className="mt-2 text-sm text-white/50">
          Alle Anfragen aus dem Kontaktformular – neueste zuerst. Sie erhalten zusätzlich eine E-Mail an info@terbeck-service.de.
        </p>

        {items === null ? (
          <p className="mt-10 text-sm font-bold tracking-[0.3em] text-white/40">WIRD GELADEN…</p>
        ) : items.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-white/10 bg-surface p-12 text-center">
            <Inbox className="h-10 w-10 text-brand-bright" />
            <p className="mt-4 text-sm text-white/50">Noch keine Anfragen eingegangen.</p>
          </div>
        ) : (
          <ul className="mt-10 space-y-4" data-testid="anfragen-list">
            {items.map((r, i) => (
              <motion.li
                key={r.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="rounded-2xl border border-white/10 bg-surface p-6"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-brand/15 px-3 py-1 text-[11px] font-extrabold tracking-wider text-brand-bright ring-1 ring-brand/30">
                      {r.service}
                    </span>
                    <span className="font-bold text-white">{r.name}</span>
                  </div>
                  <span className="text-xs text-white/40">
                    {r.timestamp ? new Date(r.timestamp).toLocaleString("de-DE") : ""}
                  </span>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-white/60">{r.message}</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a href={`tel:${String(r.phone).replace(/\s/g, "")}`} className="inline-flex items-center gap-2 text-xs font-bold text-brand-bright hover:text-white transition-colors duration-300">
                    <Phone className="h-3.5 w-3.5" /> {r.phone}
                  </a>
                  <a href={`mailto:${r.email}`} className="inline-flex items-center gap-2 text-xs font-bold text-brand-bright hover:text-white transition-colors duration-300">
                    <Mail className="h-3.5 w-3.5" /> {r.email}
                  </a>
                </div>
              </motion.li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
